import {
    calculateLinearGradientProbability,
    calculateRadialGradientProbability,
    calculateReverseRadialGradientProbability,
    calculatePerlinNoiseProbability,
    applyDitheringToProbability,
} from "./svgUtils";

// ─── Public types ────────────────────────────────────────────────────────────

export type FillPattern =
    | "even"
    | "linear-gradient"
    | "radial-gradient"
    | "reverse-radial-gradient"
    | "perlin-noise";

export interface PackingConfig {
    maxPackSize: number; // max cells per pack group (1 = no packing)
    probability: number; // 0–100: chance a cell seeds a multi-cell pack
    seed: number;
    irregularShapes: boolean;
}

/** `rectangular`: usual axis-aligned grid. `polar`: columns = angular sectors, rows = radial rings. `spiral`: row-major Archimedean spiral. `hyperbolic`: Poincaré disk, columns = sectors, rows = rings in hyperbolic radius. `sin` / `z2_over_2`: conformal maps from a z=u+iv rectangle to w-plane quads (cols×rows only). */
export type GridShape =
    | "rectangular"
    | "polar"
    | "spiral"
    | "hyperbolic"
    | "sin"
    | "z2_over_2";

export interface VariantConfig {
    id: string;
    /** Layout geometry (packing still uses the same col/row indices). Default: rectangular. */
    gridShape?: GridShape;
    /** Full rotations along the path when `gridShape` is `spiral` (default 3). */
    spiralTurns?: number;
    columns: number;
    rows: number;
    cellWidth: number;
    cellHeight: number;
    spacingX: number;
    spacingY: number;
    density: number;
    fillPattern: FillPattern;
    startPosition: string;
    endPosition: string;
    noise: number;
    dithering: number;
    packing: PackingConfig;
}

export interface LevelGroup {
    variants: VariantConfig[];
}

export type LeafColorMode = "solid" | "gradient-by-size";

/** Geometry of each leaf-level cell in the SVG (layout grid unchanged). */
export type LeafCellShape =
    | "square"
    | "circle"
    | "rounded-square"
    | "right-triangle"
    | "hollow-square";

export interface NestedGridConfig {
    nestingLevels: number; // 1–5; levelGroups[0]=leaf, [nestingLevels-1]=root
    levelGroups: LevelGroup[];
    leafColorMode: LeafColorMode;
    /** Leaf cell geometry (default: axis-aligned square). */
    leafCellShape: LeafCellShape;
    /** Solid fill when leafColorMode is "solid". */
    leafFill: string;
    /** Ordered stops (smallest → largest rendered rect) when mode is "gradient-by-size"; ≥2 colors. */
    leafGradientStops: string[];
    seed: number; // global seed for sub-variant selection
    keepAspectRatio: boolean; // preserve aspect ratio when fitting a sub-grid into a pack region
    /** When keepAspectRatio: true = scale down to fit inside cell (contain); false = fill cell, may extend (cover). */
    containAspectFit: boolean;
    /** Flip some embedded child grids horizontally (sub-grid space). */
    mirrorChildrenEnabled: boolean;
    mirrorChildrenPercent: number;
    /** Rotate some embedded child grids by 90°/180°/270° (sub-grid space). */
    rotateChildrenEnabled: boolean;
    rotateChildrenPercent: number;
    /** When true, cell spacing is treated as 0 for layout/render (variant fields unchanged). */
    noSpacing: boolean;
    /**
     * When true, nested child grids embedded in polar/spiral/hyperbolic variants are rotated to the local tangent.
     * When false, nested content stays axis-aligned in the packed region (legacy behavior).
     */
    polarSpiralEmbedRotation: boolean;
}

// ─── Internal types ───────────────────────────────────────────────────────────

interface Cell {
    col: number;
    row: number;
}

// ─── Leaf color helpers ───────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] | null {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
    const c = (x: number) =>
        Math.max(0, Math.min(255, Math.round(x)))
            .toString(16)
            .padStart(2, "0");
    return `#${c(r)}${c(g)}${c(b)}`;
}

function lerpColorHex(a: string, b: string, t: number): string {
    const A = hexToRgb(a);
    const B = hexToRgb(b);
    if (!A || !B) return hexToRgb(a) ? a : b;
    const u = Math.max(0, Math.min(1, t));
    return rgbToHex(
        A[0] + (B[0] - A[0]) * u,
        A[1] + (B[1] - A[1]) * u,
        A[2] + (B[2] - A[2]) * u,
    );
}

/** Map t in [0,1] along evenly spaced stops (first = smallest rect metric). */
export function sampleGradientStops(stops: string[], t: number): string {
    const raw = stops.filter((s) => s.trim().length > 0);
    if (raw.length === 0) return "#000000";
    if (raw.length === 1) return raw[0]!;
    const u = Math.max(0, Math.min(1, t)) * (raw.length - 1);
    const i = Math.min(Math.floor(u), raw.length - 2);
    const f = u - i;
    return lerpColorHex(raw[i]!, raw[i + 1]!, f);
}

interface PackRegion {
    id: string;
    cells: Cell[];
    minCol: number;
    maxCol: number;
    minRow: number;
    maxRow: number;
}

// ─── Seeded RNG (mulberry32) ──────────────────────────────────────────────────

function makeRng(seed: number): () => number {
    let s = (seed ^ 0xdeadbeef) >>> 0;
    return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function shuffle<T>(arr: T[], rng: () => number): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

// ─── Packing layout ───────────────────────────────────────────────────────────

export function generatePackingLayout(
    cols: number,
    rows: number,
    config: PackingConfig,
): PackRegion[] {
    const { maxPackSize, probability, seed, irregularShapes } = config;
    const rng = makeRng(seed);
    const occupied = new Set<string>();
    const regions: PackRegion[] = [];
    let rid = 0;

    const key = (c: number, r: number) => `${c},${r}`;

    const allCells: Cell[] = [];
    for (let r = 0; r < rows; r++)
        for (let c = 0; c < cols; c++) allCells.push({ col: c, row: r });

    const shuffled = shuffle(allCells, rng);

    for (const start of shuffled) {
        if (occupied.has(key(start.col, start.row))) continue;

        const cells: Cell[] = [start];
        occupied.add(key(start.col, start.row));

        if (maxPackSize > 1 && rng() * 100 < probability) {
            if (irregularShapes) {
                // Flood-fill style irregular growth
                const frontier = [start];
                while (cells.length < maxPackSize && frontier.length > 0) {
                    const fi = Math.floor(rng() * frontier.length);
                    const fc = frontier[fi];
                    const neighbors = [
                        { col: fc.col + 1, row: fc.row },
                        { col: fc.col - 1, row: fc.row },
                        { col: fc.col, row: fc.row + 1 },
                        { col: fc.col, row: fc.row - 1 },
                    ].filter(
                        (n) =>
                            n.col >= 0 &&
                            n.col < cols &&
                            n.row >= 0 &&
                            n.row < rows &&
                            !occupied.has(key(n.col, n.row)),
                    );
                    if (neighbors.length === 0) {
                        frontier.splice(fi, 1);
                        continue;
                    }
                    const chosen =
                        neighbors[Math.floor(rng() * neighbors.length)];
                    cells.push(chosen);
                    frontier.push(chosen);
                    occupied.add(key(chosen.col, chosen.row));
                }
            } else {
                // Rectangular growth: expand right or down
                let minC = start.col,
                    maxC = start.col;
                let minR = start.row,
                    maxR = start.row;

                while (cells.length < maxPackSize) {
                    const dirs: ("right" | "down")[] = [];
                    if (maxC + 1 < cols) {
                        let ok = true;
                        for (let r = minR; r <= maxR; r++) {
                            if (occupied.has(key(maxC + 1, r))) {
                                ok = false;
                                break;
                            }
                        }
                        if (ok) dirs.push("right");
                    }
                    if (maxR + 1 < rows) {
                        let ok = true;
                        for (let c = minC; c <= maxC; c++) {
                            if (occupied.has(key(c, maxR + 1))) {
                                ok = false;
                                break;
                            }
                        }
                        if (ok) dirs.push("down");
                    }
                    if (dirs.length === 0) break;

                    const dir = dirs[Math.floor(rng() * dirs.length)];
                    if (dir === "right") {
                        for (
                            let r = minR;
                            r <= maxR && cells.length < maxPackSize;
                            r++
                        ) {
                            const nc = { col: maxC + 1, row: r };
                            cells.push(nc);
                            occupied.add(key(nc.col, nc.row));
                        }
                        maxC++;
                    } else {
                        for (
                            let c = minC;
                            c <= maxC && cells.length < maxPackSize;
                            c++
                        ) {
                            const nc = { col: c, row: maxR + 1 };
                            cells.push(nc);
                            occupied.add(key(nc.col, nc.row));
                        }
                        maxR++;
                    }
                }
            }
        }

        regions.push({
            id: `r${rid++}`,
            cells,
            minCol: Math.min(...cells.map((c) => c.col)),
            maxCol: Math.max(...cells.map((c) => c.col)),
            minRow: Math.min(...cells.map((c) => c.row)),
            maxRow: Math.max(...cells.map((c) => c.row)),
        });
    }

    return regions;
}

// ─── Coordinate helpers ───────────────────────────────────────────────────────

function cellPx(
    col: number,
    row: number,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
) {
    return { x: col * (cw + sX), y: row * (ch + sY) };
}

function gridDim(count: number, cellSize: number, spacing: number): number {
    const n = Math.max(1, count);
    return n * cellSize + Math.max(0, n - 1) * spacing;
}

// ─── Polar layout (columns = sectors around center, rows = rings) ─────────────

/** SVG-style rotate(deg): x' = x cos θ − y sin θ, y' = x sin θ + y cos θ (clockwise for positive deg). */
function svgRotatePoint(lx: number, ly: number, rotDeg: number): [number, number] {
    const rad = (rotDeg * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    return [lx * c - ly * s, lx * s + ly * c];
}

function rotatedRectAabb(
    cx: number,
    cy: number,
    cw: number,
    ch: number,
    rotDeg: number,
): { minX: number; minY: number; maxX: number; maxY: number } {
    const corners: [number, number][] = [
        [-cw / 2, -ch / 2],
        [cw / 2, -ch / 2],
        [cw / 2, ch / 2],
        [-cw / 2, ch / 2],
    ];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [lx, ly] of corners) {
        const [wx, wy] = svgRotatePoint(lx, ly, rotDeg);
        const x = wx + cx;
        const y = wy + cy;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
    }
    return { minX, minY, maxX, maxY };
}

/** Smallest inner radius so the innermost ring can fit cell tangential width `cw`. */
function polarInnerRadius(cols: number, cw: number, ch: number): number {
    const c = Math.max(1, cols);
    const need = (cw * c) / (2 * Math.PI) - ch / 2;
    return Math.max(0, need);
}

export interface PolarLayoutMetrics {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    innerR: number;
}

function polarCellCenterRaw(
    col: number,
    row: number,
    cols: number,
    cw: number,
    ch: number,
    sY: number,
    innerR: number,
): { cx: number; cy: number; rotDeg: number } {
    const c = Math.max(1, cols);
    const rowPitch = ch + sY;
    const R = innerR + row * rowPitch + ch / 2;
    const theta = -Math.PI / 2 + ((col + 0.5) * (2 * Math.PI)) / c;
    const rotDeg = (theta * 180) / Math.PI + 90;
    return {
        cx: R * Math.cos(theta),
        cy: R * Math.sin(theta),
        rotDeg,
    };
}

function computePolarLayoutMetrics(
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
): PolarLayoutMetrics {
    const innerR = polarInnerRadius(cols, cw, ch);
    const c = Math.max(1, cols);
    const r = Math.max(1, rows);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let row = 0; row < r; row++) {
        for (let col = 0; col < c; col++) {
            const { cx, cy, rotDeg } = polarCellCenterRaw(
                col,
                row,
                c,
                cw,
                ch,
                sY,
                innerR,
            );
            const box = rotatedRectAabb(cx, cy, cw, ch, rotDeg);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        }
    }
    const width = maxX - minX;
    const height = maxY - minY;
    return {
        offsetX: minX,
        offsetY: minY,
        width,
        height,
        innerR,
    };
}

function polarCellInVariantSpace(
    col: number,
    row: number,
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
    m: PolarLayoutMetrics,
): { cx: number; cy: number; rotDeg: number } {
    const raw = polarCellCenterRaw(
        col,
        row,
        cols,
        cw,
        ch,
        sY,
        m.innerR,
    );
    return {
        cx: raw.cx - m.offsetX,
        cy: raw.cy - m.offsetY,
        rotDeg: raw.rotDeg,
    };
}

// ─── Hyperbolic disk (Poincaré): ρ in hyperbolic space, r_E = tanh(ρ/2) ───────

/** Ring centers use ρ ∈ [rhoMin, rhoMax]; positions scaled so outer ring matches polar outer radius. */
export interface HyperbolicLayoutMetrics {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    rhoMin: number;
    rhoMax: number;
    scale: number;
    cols: number;
    rows: number;
}

function hyperbolicRhoAtRowCenter(
    row: number,
    rows: number,
    rhoMin: number,
    rhoMax: number,
): number {
    const r = Math.max(1, rows);
    return rhoMin + ((row + 0.5) / r) * (rhoMax - rhoMin);
}

function hyperbolicEuclideanRFromRho(rho: number): number {
    return Math.tanh(rho / 2);
}

function hyperbolicCellCenterRaw(
    col: number,
    row: number,
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sY: number,
    rhoMin: number,
    rhoMax: number,
    scale: number,
): { cx: number; cy: number; rotDeg: number } {
    const c = Math.max(1, cols);
    const rho = hyperbolicRhoAtRowCenter(row, rows, rhoMin, rhoMax);
    const rE = hyperbolicEuclideanRFromRho(rho) * scale;
    const theta = -Math.PI / 2 + ((col + 0.5) * (2 * Math.PI)) / c;
    const rotDeg = (theta * 180) / Math.PI + 90;
    return {
        cx: rE * Math.cos(theta),
        cy: rE * Math.sin(theta),
        rotDeg,
    };
}

function computeHyperbolicLayoutMetrics(
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
): HyperbolicLayoutMetrics {
    const c = Math.max(1, cols);
    const rw = Math.max(1, rows);
    const rowPitch = ch + sY;
    const innerR = polarInnerRadius(cols, cw, ch);
    const polarOuterR = innerR + (rw - 1) * rowPitch + ch / 2;
    /** Hyperbolic radius range (larger ρ → closer to the ideal boundary). */
    const rhoMin = 0.2;
    const rhoMax = 2.8 + Math.log2(rw);
    const rhoOut = hyperbolicRhoAtRowCenter(rw - 1, rw, rhoMin, rhoMax);
    const rOutHyp = hyperbolicEuclideanRFromRho(rhoOut);
    const scale =
        rOutHyp > 1e-9 ? polarOuterR / rOutHyp : polarOuterR;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let row = 0; row < rw; row++) {
        for (let col = 0; col < c; col++) {
            const { cx, cy, rotDeg } = hyperbolicCellCenterRaw(
                col,
                row,
                c,
                rw,
                cw,
                ch,
                sY,
                rhoMin,
                rhoMax,
                scale,
            );
            const box = rotatedRectAabb(cx, cy, cw, ch, rotDeg);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        }
    }
    return {
        offsetX: minX,
        offsetY: minY,
        width: maxX - minX,
        height: maxY - minY,
        rhoMin,
        rhoMax,
        scale,
        cols: c,
        rows: rw,
    };
}

function hyperbolicCellInVariantSpace(
    col: number,
    row: number,
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sY: number,
    m: HyperbolicLayoutMetrics,
): { cx: number; cy: number; rotDeg: number } {
    const raw = hyperbolicCellCenterRaw(
        col,
        row,
        cols,
        rows,
        cw,
        ch,
        sY,
        m.rhoMin,
        m.rhoMax,
        m.scale,
    );
    return {
        cx: raw.cx - m.offsetX,
        cy: raw.cy - m.offsetY,
        rotDeg: raw.rotDeg,
    };
}

// ─── Spiral layout (row-major index follows a growing spiral) ────────────────

export interface SpiralLayoutMetrics {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    cols: number;
    rows: number;
    numTurns: number;
    r0: number;
    dr: number;
}

function spiralPoint(
    i: number,
    N: number,
    numTurns: number,
    r0: number,
    dr: number,
): { x: number; y: number } {
    const dTheta =
        N <= 1 ? 0 : (2 * Math.PI * numTurns) / Math.max(1, N - 1);
    const theta = -Math.PI / 2 + i * dTheta;
    const r = r0 + i * dr;
    return {
        x: r * Math.cos(theta),
        y: r * Math.sin(theta),
    };
}

function spiralRotDegFromMetrics(
    i: number,
    N: number,
    sm: Pick<SpiralLayoutMetrics, "numTurns" | "r0" | "dr">,
): number {
    if (N <= 1) return 0;
    const p0 = spiralPoint(i, N, sm.numTurns, sm.r0, sm.dr);
    let vx: number;
    let vy: number;
    if (i < N - 1) {
        const p1 = spiralPoint(i + 1, N, sm.numTurns, sm.r0, sm.dr);
        vx = p1.x - p0.x;
        vy = p1.y - p0.y;
    } else {
        const pm = spiralPoint(i - 1, N, sm.numTurns, sm.r0, sm.dr);
        vx = p0.x - pm.x;
        vy = p0.y - pm.y;
    }
    return (Math.atan2(vy, vx) * 180) / Math.PI + 90;
}

function spiralCellCenterRaw(
    col: number,
    row: number,
    sm: SpiralLayoutMetrics,
): { cx: number; cy: number; rotDeg: number } {
    const i = row * sm.cols + col;
    const N = sm.cols * sm.rows;
    const p = spiralPoint(i, N, sm.numTurns, sm.r0, sm.dr);
    const rotDeg = spiralRotDegFromMetrics(i, N, sm);
    return { cx: p.x, cy: p.y, rotDeg };
}

function computeSpiralLayoutMetrics(
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
    spiralTurns: number,
): SpiralLayoutMetrics {
    const c = Math.max(1, cols);
    const rw = Math.max(1, rows);
    const N = c * rw;
    const cellNom = Math.max(cw, ch) + Math.max(sY, sX);
    const r0 = Math.max(cellNom * 0.35, cw / 2);
    const dr = cellNom * 0.42;
    const numTurns = Math.max(0.25, spiralTurns);

    const sm: SpiralLayoutMetrics = {
        offsetX: 0,
        offsetY: 0,
        width: 0,
        height: 0,
        cols: c,
        rows: rw,
        numTurns,
        r0,
        dr,
    };

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let row = 0; row < rw; row++) {
        for (let col = 0; col < c; col++) {
            const { cx, cy, rotDeg } = spiralCellCenterRaw(col, row, sm);
            const box = rotatedRectAabb(cx, cy, cw, ch, rotDeg);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        }
    }
    return {
        ...sm,
        offsetX: minX,
        offsetY: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

function spiralCellInVariantSpace(
    col: number,
    row: number,
    m: SpiralLayoutMetrics,
): { cx: number; cy: number; rotDeg: number } {
    const raw = spiralCellCenterRaw(col, row, m);
    return {
        cx: raw.cx - m.offsetX,
        cy: raw.cy - m.offsetY,
        rotDeg: raw.rotDeg,
    };
}

// ─── sin(z) conformal layout (z = u + iv in parameter plane) ────────────────

/** Image plane: x + iy = sin(u + iv). */
function sinZPlane(u: number, v: number): { x: number; y: number } {
    return {
        x: Math.sin(u) * Math.cosh(v),
        y: Math.cos(u) * Math.sinh(v),
    };
}

/** z-plane domain split into cols×rows (shared by sin(z) and z²/2 conformal quads). */
export interface ConformalZDomainMetrics {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    cols: number;
    rows: number;
    uMin: number;
    uMax: number;
    vMin: number;
    vMax: number;
}

/** Alias of ConformalZDomainMetrics (sin(z) layout). */
export type SinLayoutMetrics = ConformalZDomainMetrics;
export type Z2HalfLayoutMetrics = ConformalZDomainMetrics;

/** z-plane rectangle for one cell (col = Re z index, row = Im z index). */
function conformalUvCellBounds(
    col: number,
    row: number,
    m: ConformalZDomainMetrics,
): { u0: number; u1: number; v0: number; v1: number } {
    const c = Math.max(1, m.cols);
    const r = Math.max(1, m.rows);
    const u0 = m.uMin + (col / c) * (m.uMax - m.uMin);
    const u1 = m.uMin + ((col + 1) / c) * (m.uMax - m.uMin);
    const v0 = m.vMin + (row / r) * (m.vMax - m.vMin);
    const v1 = m.vMin + ((row + 1) / r) * (m.vMax - m.vMin);
    return { u0, u1, v0, v1 };
}

/** w = z²/2 with z = u + iv → (u²−v²)/2 + i·uv. */
function z2HalfZPlane(u: number, v: number): { x: number; y: number } {
    return {
        x: (u * u - v * v) / 2,
        y: u * v,
    };
}

export type ConformalMapKind = "sin" | "z2half";

function conformalPlanePoint(
    kind: ConformalMapKind,
    u: number,
    v: number,
): { x: number; y: number } {
    return kind === "sin" ? sinZPlane(u, v) : z2HalfZPlane(u, v);
}

/** Image of one z-rectangle under sin(z) or z²/2; four corners in variant (offset) coordinates. */
function conformalQuadCornersVariantSpace(
    kind: ConformalMapKind,
    col: number,
    row: number,
    m: ConformalZDomainMetrics,
): { x: number; y: number }[] {
    const { u0, u1, v0, v1 } = conformalUvCellBounds(col, row, m);
    const raw = [
        conformalPlanePoint(kind, u0, v0),
        conformalPlanePoint(kind, u1, v0),
        conformalPlanePoint(kind, u1, v1),
        conformalPlanePoint(kind, u0, v1),
    ];
    return raw.map((p) => ({
        x: p.x - m.offsetX,
        y: p.y - m.offsetY,
    }));
}

function aabbOfPoints(
    pts: { x: number; y: number }[],
): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of pts) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    }
    return { minX, minY, maxX, maxY };
}

/** Axis-aligned bbox area after linear map M (same idea as aabbAreaFromRect for gradient). */
function aabbAreaFromPoints(
    pts: { x: number; y: number }[],
    M: Mat2,
): number {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of pts) {
        const xp = M.m00 * p.x + M.m01 * p.y;
        const yp = M.m10 * p.x + M.m11 * p.y;
        minX = Math.min(minX, xp);
        minY = Math.min(minY, yp);
        maxX = Math.max(maxX, xp);
        maxY = Math.max(maxY, yp);
    }
    const rw = maxX - minX;
    const rh = maxY - minY;
    return rw * rh;
}

function computeSinLayoutMetrics(
    cols: number,
    rows: number,
    _cw: number,
    _ch: number,
    _sX: number,
    _sY: number,
): ConformalZDomainMetrics {
    const c = Math.max(1, cols);
    const rw = Math.max(1, rows);
    /**
     * Classic sin(z) plot: Re(z) in open strip, Im(z) in [-2, 2] gives ellipse / hyperbola grid
     * like textbook conformal figures (cf. -2…2 image axes).
     */
    const uMin = -Math.PI / 2 + 0.08;
    const uMax = Math.PI / 2 - 0.08;
    const vMin = -2;
    const vMax = 2;

    const sm: ConformalZDomainMetrics = {
        offsetX: 0,
        offsetY: 0,
        width: 0,
        height: 0,
        cols: c,
        rows: rw,
        uMin,
        uMax,
        vMin,
        vMax,
    };

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let row = 0; row < rw; row++) {
        for (let col = 0; col < c; col++) {
            const { u0, u1, v0, v1 } = conformalUvCellBounds(col, row, sm);
            for (const p of [
                conformalPlanePoint("sin", u0, v0),
                conformalPlanePoint("sin", u1, v0),
                conformalPlanePoint("sin", u1, v1),
                conformalPlanePoint("sin", u0, v1),
            ]) {
                minX = Math.min(minX, p.x);
                minY = Math.min(minY, p.y);
                maxX = Math.max(maxX, p.x);
                maxY = Math.max(maxY, p.y);
            }
        }
    }
    return {
        ...sm,
        offsetX: minX,
        offsetY: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

function computeZ2HalfLayoutMetrics(
    cols: number,
    rows: number,
    _cw: number,
    _ch: number,
    _sX: number,
    _sY: number,
): ConformalZDomainMetrics {
    const c = Math.max(1, cols);
    const rw = Math.max(1, rows);
    /** Square z-domain u+iv ∈ [-2,2]² as in common z²/2 conformal figures. */
    const uMin = -2;
    const uMax = 2;
    const vMin = -2;
    const vMax = 2;

    const zm: ConformalZDomainMetrics = {
        offsetX: 0,
        offsetY: 0,
        width: 0,
        height: 0,
        cols: c,
        rows: rw,
        uMin,
        uMax,
        vMin,
        vMax,
    };

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let row = 0; row < rw; row++) {
        for (let col = 0; col < c; col++) {
            const { u0, u1, v0, v1 } = conformalUvCellBounds(col, row, zm);
            for (const p of [
                conformalPlanePoint("z2half", u0, v0),
                conformalPlanePoint("z2half", u1, v0),
                conformalPlanePoint("z2half", u1, v1),
                conformalPlanePoint("z2half", u0, v1),
            ]) {
                minX = Math.min(minX, p.x);
                minY = Math.min(minY, p.y);
                maxX = Math.max(maxX, p.x);
                maxY = Math.max(maxY, p.y);
            }
        }
    }
    return {
        ...zm,
        offsetX: minX,
        offsetY: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

/** Per-variant layout: rectangular grid, polar ring, spiral path, hyperbolic disk, or conformal quads. */
type VariantLayoutExtra =
    | { kind: "rect" }
    | { kind: "polar"; m: PolarLayoutMetrics }
    | { kind: "spiral"; m: SpiralLayoutMetrics }
    | { kind: "hyperbolic"; m: HyperbolicLayoutMetrics }
    | { kind: "sin"; m: ConformalZDomainMetrics }
    | { kind: "z2half"; m: ConformalZDomainMetrics };

function variantLayoutExtraForVariant(
    v: Pick<
        VariantConfig,
        | "gridShape"
        | "columns"
        | "rows"
        | "cellWidth"
        | "cellHeight"
        | "spiralTurns"
    >,
    sX: number,
    sY: number,
): VariantLayoutExtra {
    const g = v.gridShape ?? "rectangular";
    if (g === "polar") {
        return {
            kind: "polar",
            m: computePolarLayoutMetrics(
                Math.max(1, v.columns),
                Math.max(1, v.rows),
                v.cellWidth,
                v.cellHeight,
                sX,
                sY,
            ),
        };
    }
    if (g === "spiral") {
        return {
            kind: "spiral",
            m: computeSpiralLayoutMetrics(
                Math.max(1, v.columns),
                Math.max(1, v.rows),
                v.cellWidth,
                v.cellHeight,
                sX,
                sY,
                v.spiralTurns ?? 3,
            ),
        };
    }
    if (g === "hyperbolic") {
        return {
            kind: "hyperbolic",
            m: computeHyperbolicLayoutMetrics(
                Math.max(1, v.columns),
                Math.max(1, v.rows),
                v.cellWidth,
                v.cellHeight,
                sX,
                sY,
            ),
        };
    }
    if (g === "sin") {
        return {
            kind: "sin",
            m: computeSinLayoutMetrics(
                Math.max(1, v.columns),
                Math.max(1, v.rows),
                v.cellWidth,
                v.cellHeight,
                sX,
                sY,
            ),
        };
    }
    if (g === "z2_over_2") {
        return {
            kind: "z2half",
            m: computeZ2HalfLayoutMetrics(
                Math.max(1, v.columns),
                Math.max(1, v.rows),
                v.cellWidth,
                v.cellHeight,
                sX,
                sY,
            ),
        };
    }
    return { kind: "rect" };
}

function rotatedLeafPlacement(
    layout: VariantLayoutExtra,
    col: number,
    row: number,
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
): { cx: number; cy: number; rotDeg: number } | null {
    if (layout.kind === "polar") {
        return polarCellInVariantSpace(
            col,
            row,
            cols,
            rows,
            cw,
            ch,
            sX,
            sY,
            layout.m,
        );
    }
    if (layout.kind === "spiral") {
        return spiralCellInVariantSpace(col, row, layout.m);
    }
    if (layout.kind === "hyperbolic") {
        return hyperbolicCellInVariantSpace(
            col,
            row,
            cols,
            rows,
            cw,
            ch,
            sY,
            layout.m,
        );
    }
    return null;
}

/**
 * Tangent rotation at the center of a packed region when the parent uses polar/spiral/hyperbolic.
 * Used to rotate embedded child grids so they align with the ring/spiral (non-leaf levels).
 */
function layoutEmbeddingRotationDeg(
    layout: VariantLayoutExtra,
    region: PackRegion,
    cols: number,
    rows: number,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
): number {
    if (layout.kind === "polar") {
        const cc = Math.round((region.minCol + region.maxCol) / 2);
        const cr = Math.round((region.minRow + region.maxRow) / 2);
        return polarCellInVariantSpace(
            cc,
            cr,
            cols,
            rows,
            cw,
            ch,
            sX,
            sY,
            layout.m,
        ).rotDeg;
    }
    if (layout.kind === "spiral") {
        const cc = Math.round((region.minCol + region.maxCol) / 2);
        const cr = Math.round((region.minRow + region.maxRow) / 2);
        return spiralCellInVariantSpace(cc, cr, layout.m).rotDeg;
    }
    if (layout.kind === "hyperbolic") {
        const cc = Math.round((region.minCol + region.maxCol) / 2);
        const cr = Math.round((region.minRow + region.maxRow) / 2);
        return hyperbolicCellInVariantSpace(
            cc,
            cr,
            cols,
            rows,
            cw,
            ch,
            sY,
            layout.m,
        ).rotDeg;
    }
    return 0;
}

/** Center of the scaled sub-grid rectangle inside `bbox` (variant space). */
function embeddingPlacementCenter(
    bbox: { x: number; y: number; width: number; height: number },
    keepAspectRatio: boolean,
    containAspectFit: boolean,
    subW: number,
    subH: number,
    scaleX: number,
    scaleY: number,
): { cx: number; cy: number } {
    if (!keepAspectRatio) {
        return {
            cx: bbox.x + (subW * scaleX) / 2,
            cy: bbox.y + (subH * scaleY) / 2,
        };
    }
    const sc = containAspectFit
        ? Math.min(scaleX, scaleY)
        : Math.max(scaleX, scaleY);
    const scaledW = subW * sc;
    const scaledH = subH * sc;
    const offsetX = bbox.x + (bbox.width - scaledW) / 2;
    const offsetY = bbox.y + (bbox.height - scaledH) / 2;
    return {
        cx: offsetX + scaledW / 2,
        cy: offsetY + scaledH / 2,
    };
}

function regionBBox(
    r: PackRegion,
    cw: number,
    ch: number,
    sX: number,
    sY: number,
    cols: number,
    rows: number,
    layout: VariantLayoutExtra,
) {
    if (layout.kind === "rect") {
        const { x: x1, y: y1 } = cellPx(r.minCol, r.minRow, cw, ch, sX, sY);
        const x2 = r.maxCol * (cw + sX) + cw;
        const y2 = r.maxRow * (ch + sY) + ch;
        return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
    }
    const c = Math.max(1, cols);
    const rw = Math.max(1, rows);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    if (layout.kind === "polar") {
        for (const cell of r.cells) {
            const { cx, cy, rotDeg } = polarCellInVariantSpace(
                cell.col,
                cell.row,
                c,
                rw,
                cw,
                ch,
                sX,
                sY,
                layout.m,
            );
            const box = rotatedRectAabb(cx, cy, cw, ch, rotDeg);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        }
    } else if (layout.kind === "spiral") {
        for (const cell of r.cells) {
            const { cx, cy, rotDeg } = spiralCellInVariantSpace(
                cell.col,
                cell.row,
                layout.m,
            );
            const box = rotatedRectAabb(cx, cy, cw, ch, rotDeg);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        }
    } else if (layout.kind === "hyperbolic") {
        for (const cell of r.cells) {
            const { cx, cy, rotDeg } = hyperbolicCellInVariantSpace(
                cell.col,
                cell.row,
                c,
                rw,
                cw,
                ch,
                sY,
                layout.m,
            );
            const box = rotatedRectAabb(cx, cy, cw, ch, rotDeg);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        }
    } else if (layout.kind === "sin" || layout.kind === "z2half") {
        const kind: ConformalMapKind =
            layout.kind === "sin" ? "sin" : "z2half";
        for (const cell of r.cells) {
            const corners = conformalQuadCornersVariantSpace(
                kind,
                cell.col,
                cell.row,
                layout.m,
            );
            const box = aabbOfPoints(corners);
            minX = Math.min(minX, box.minX);
            minY = Math.min(minY, box.minY);
            maxX = Math.max(maxX, box.maxX);
            maxY = Math.max(maxY, box.maxY);
        }
    }
    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

function variantLayoutSize(
    v: Pick<
        VariantConfig,
        | "gridShape"
        | "columns"
        | "rows"
        | "cellWidth"
        | "cellHeight"
        | "spiralTurns"
    >,
    sX: number,
    sY: number,
): { w: number; h: number } {
    const cols = Math.max(1, v.columns);
    const rows = Math.max(1, v.rows);
    const g = v.gridShape ?? "rectangular";
    if (g === "rectangular") {
        return {
            w: gridDim(cols, v.cellWidth, sX),
            h: gridDim(rows, v.cellHeight, sY),
        };
    }
    if (g === "polar") {
        const m = computePolarLayoutMetrics(
            cols,
            rows,
            v.cellWidth,
            v.cellHeight,
            sX,
            sY,
        );
        return { w: m.width, h: m.height };
    }
    if (g === "spiral") {
        const m = computeSpiralLayoutMetrics(
            cols,
            rows,
            v.cellWidth,
            v.cellHeight,
            sX,
            sY,
            v.spiralTurns ?? 3,
        );
        return { w: m.width, h: m.height };
    }
    if (g === "hyperbolic") {
        const m = computeHyperbolicLayoutMetrics(
            cols,
            rows,
            v.cellWidth,
            v.cellHeight,
            sX,
            sY,
        );
        return { w: m.width, h: m.height };
    }
    if (g === "sin") {
        const m = computeSinLayoutMetrics(
            cols,
            rows,
            v.cellWidth,
            v.cellHeight,
            sX,
            sY,
        );
        return { w: m.width, h: m.height };
    }
    if (g === "z2_over_2") {
        const m = computeZ2HalfLayoutMetrics(
            cols,
            rows,
            v.cellWidth,
            v.cellHeight,
            sX,
            sY,
        );
        return { w: m.width, h: m.height };
    }
    return {
        w: gridDim(cols, v.cellWidth, sX),
        h: gridDim(rows, v.cellHeight, sY),
    };
}

/** Leaf cell: axis-aligned rect, or rotated placement (polar / spiral / hyperbolic). */
function leafCellSvgPlaced(
    cw: number,
    ch: number,
    color: string,
    shape: LeafCellShape,
    placement:
        | { kind: "rect"; x: number; y: number }
        | { kind: "rotated"; cx: number; cy: number; rotDeg: number },
): string {
    if (placement.kind === "rect") {
        return leafCellSvg(placement.x, placement.y, cw, ch, color, shape);
    }
    const inner = leafCellSvg(0, 0, cw, ch, color, shape);
    return `<g transform="translate(${fmt(placement.cx)},${fmt(
        placement.cy,
    )}) rotate(${fmt(placement.rotDeg)}) translate(${fmt(-cw / 2)},${fmt(
        -ch / 2,
    )})">${inner}</g>`;
}

/** Filled / stroked sin(z) cell: image of a z-rectangle (curved quad in w-plane). */
function leafSinCellSvg(
    corners: { x: number; y: number }[],
    color: string,
    shape: LeafCellShape,
): string {
    const pts = corners.map((p) => `${fmt(p.x)},${fmt(p.y)}`).join(" ");
    switch (shape) {
        case "hollow-square": {
            const sw = Math.max(
                1,
                Math.min(
                    ...corners.map((a, i) => {
                        const b = corners[(i + 1) % 4]!;
                        return Math.hypot(b.x - a.x, b.y - a.y);
                    }),
                ) * 0.06,
            );
            return `<polygon points="${pts}" fill="none" stroke="${color}" stroke-width="${fmt(
                sw,
            )}"/>`;
        }
        case "circle": {
            let sx = 0;
            let sy = 0;
            for (const p of corners) {
                sx += p.x;
                sy += p.y;
            }
            const cx = sx / 4;
            const cy = sy / 4;
            const rx =
                0.25 *
                (Math.hypot(corners[0]!.x - cx, corners[0]!.y - cy) +
                    Math.hypot(corners[1]!.x - cx, corners[1]!.y - cy));
            const ry =
                0.25 *
                (Math.hypot(corners[1]!.x - cx, corners[1]!.y - cy) +
                    Math.hypot(corners[2]!.x - cx, corners[2]!.y - cy));
            return `<ellipse cx="${fmt(cx)}" cy="${fmt(cy)}" rx="${fmt(
                rx,
            )}" ry="${fmt(ry)}" fill="${color}"/>`;
        }
        case "right-triangle": {
            const [a, b, c] = corners;
            return `<polygon points="${fmt(a!.x)},${fmt(a!.y)} ${fmt(
                b!.x,
            )},${fmt(b!.y)} ${fmt(c!.x)},${fmt(c!.y)}" fill="${color}"/>`;
        }
        case "rounded-square":
        default:
            return `<polygon points="${pts}" fill="${color}"/>`;
    }
}

function fmt(n: number): number {
    return parseFloat(n.toFixed(4));
}

/** Prepend rotate(rotDeg) around (cx,cy) to an SVG transform list (rightmost applies first). */
function wrapEmbeddingTransform(
    cx: number,
    cy: number,
    rotDeg: number,
    innerTransform: string,
): string {
    if (Math.abs(rotDeg) < 1e-9) return innerTransform;
    return `translate(${fmt(cx)},${fmt(cy)}) rotate(${fmt(
        rotDeg,
    )}) translate(${fmt(-cx)},${fmt(-cy)}) ${innerTransform}`;
}

function leafCellSvg(
    x: number,
    y: number,
    cw: number,
    ch: number,
    color: string,
    shape: LeafCellShape,
): string {
    switch (shape) {
        case "square":
            return `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(cw)}" height="${fmt(ch)}" fill="${color}"/>`;
        case "circle": {
            const cx = x + cw / 2;
            const cy = y + ch / 2;
            return `<ellipse cx="${fmt(cx)}" cy="${fmt(cy)}" rx="${fmt(
                cw / 2,
            )}" ry="${fmt(ch / 2)}" fill="${color}"/>`;
        }
        case "rounded-square": {
            const r = Math.min(cw, ch) * 0.22;
            const rr = Math.min(r, cw / 2, ch / 2);
            return `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(
                cw,
            )}" height="${fmt(ch)}" rx="${fmt(rr)}" ry="${fmt(rr)}" fill="${color}"/>`;
        }
        case "right-triangle": {
            // Right angle at bottom-left of the cell
            const x1 = x;
            const y1 = y + ch;
            const x2 = x + cw;
            const y2 = y + ch;
            const x3 = x;
            const y3 = y;
            return `<polygon points="${fmt(x1)},${fmt(y1)} ${fmt(x2)},${fmt(
                y2,
            )} ${fmt(x3)},${fmt(y3)}" fill="${color}"/>`;
        }
        case "hollow-square": {
            const sw = Math.max(1, Math.min(cw, ch) * 0.08);
            return `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(
                cw,
            )}" height="${fmt(ch)}" fill="none" stroke="${color}" stroke-width="${fmt(
                sw,
            )}"/>`;
        }
        default:
            return `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(
                cw,
            )}" height="${fmt(ch)}" fill="${color}"/>`;
    }
}

// ─── 2×2 linear transforms (leaf vector → root SVG); column vector [x,y]

interface Mat2 {
    m00: number;
    m01: number;
    m10: number;
    m11: number;
}

function matIdentity(): Mat2 {
    return { m00: 1, m01: 0, m10: 0, m11: 1 };
}

function matMul(A: Mat2, B: Mat2): Mat2 {
    return {
        m00: A.m00 * B.m00 + A.m01 * B.m10,
        m01: A.m00 * B.m01 + A.m01 * B.m11,
        m10: A.m10 * B.m00 + A.m11 * B.m10,
        m11: A.m10 * B.m01 + A.m11 * B.m11,
    };
}

/** SVG rotate(deg) linear part (y-down): MDN matrix(a,b,c,d) with x'=ax+cy, y'=bx+dy. */
function matRotateSvg(deg: 0 | 90 | 180 | 270): Mat2 {
    if (deg === 0) return matIdentity();
    if (deg === 90) return { m00: 0, m01: -1, m10: 1, m11: 0 };
    if (deg === 180) return { m00: -1, m01: 0, m10: 0, m11: -1 };
    return { m00: 0, m01: 1, m10: -1, m11: 0 }; // 270
}

/** Arbitrary SVG rotation (degrees, +y down); matches `rotate(deg)` linear part. */
function matRotate2dDeg(deg: number): Mat2 {
    const rad = (deg * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    return { m00: c, m01: s, m10: -s, m11: c };
}

function matMirrorX(): Mat2 {
    return { m00: -1, m01: 0, m10: 0, m11: 1 };
}

function matScale(sx: number, sy: number): Mat2 {
    return { m00: sx, m01: 0, m10: 0, m11: sy };
}

/** Linear part of one child embedding: scale, then optional mirror/rotate on sub-grid (matches wrapChildGridDecorations order). */
function composeChildEmbeddingLinear(
    sx: number,
    sy: number,
    rotateDeg: 0 | 90 | 180 | 270,
    doMirror: boolean,
): Mat2 {
    let L = matRotateSvg(rotateDeg);
    if (doMirror) L = matMul(matMirrorX(), L);
    L = matMul(matScale(sx, sy), L);
    return L;
}

/** Axis-aligned bbox area of a cw×ch rect at the origin after linear map M. */
function aabbAreaFromRect(cw: number, ch: number, M: Mat2): number {
    const corners: [number, number][] = [
        [0, 0],
        [cw, 0],
        [0, ch],
        [cw, ch],
    ];
    const xs = corners.map(([x, y]) => M.m00 * x + M.m01 * y);
    const ys = corners.map(([x, y]) => M.m10 * x + M.m11 * y);
    const rw = Math.max(...xs) - Math.min(...xs);
    const rh = Math.max(...ys) - Math.min(...ys);
    return rw * rh;
}

/** Advance global RNG like the main precompute loop, stopping before (rootLvl, vi=0). */
function fastForwardGlobalRngBeforeRootVariant0(
    globalRng: () => number,
    config: NestedGridConfig,
): void {
    const {
        nestingLevels,
        levelGroups,
        noSpacing,
        mirrorChildrenEnabled,
        mirrorChildrenPercent,
        rotateChildrenEnabled,
        rotateChildrenPercent,
    } = config;
    const rootLvl = nestingLevels - 1;

    for (let lvl = 0; lvl < nestingLevels; lvl++) {
        const group = levelGroups[lvl];
        if (!group?.variants.length) continue;
        for (let vi = 0; vi < group.variants.length; vi++) {
            if (lvl === rootLvl && vi === 0) return;

            const v = group.variants[vi]!;
            const {
                cellWidth: cw,
                cellHeight: ch,
                spacingX: rawSX,
                spacingY: rawSY,
                columns: cols,
                rows,
            } = v;
            const sX = noSpacing ? 0 : rawSX;
            const sY = noSpacing ? 0 : rawSY;
            const varSeed = v.packing.seed ^ ((vi * 997 + lvl * 31337) | 0);
            const varRng = makeRng(varSeed);
            const regions = generatePackingLayout(cols, rows, {
                ...v.packing,
                seed: varSeed,
            });
            const filled = selectFilledRegions(regions, v, varRng);

            if (lvl === 0) continue;

            const subGroup = levelGroups[lvl - 1];
            for (const region of regions) {
                if (!filled.has(region.id)) continue;
                if (!subGroup?.variants.length) continue;
                globalRng();
                if (mirrorChildrenEnabled) globalRng();
                if (rotateChildrenEnabled) {
                    const doRot = globalRng() * 100 < rotateChildrenPercent;
                    if (doRot) globalRng();
                }
            }
        }
    }
}

function collectRenderedRectAreas(
    globalRng: () => number,
    config: NestedGridConfig,
    lvl: number,
    vi: number,
    M: Mat2,
): number[] {
    const {
        levelGroups,
        noSpacing,
        keepAspectRatio,
        containAspectFit,
        mirrorChildrenEnabled,
        mirrorChildrenPercent,
        rotateChildrenEnabled,
        rotateChildrenPercent,
        polarSpiralEmbedRotation,
    } = config;
    const group = levelGroups[lvl];
    const v = group?.variants[vi];
    if (!v) return [];

    const {
        cellWidth: cw,
        cellHeight: ch,
        spacingX: rawSX,
        spacingY: rawSY,
        columns: cols,
        rows,
    } = v;
    const sX = noSpacing ? 0 : rawSX;
    const sY = noSpacing ? 0 : rawSY;
    const layout = variantLayoutExtraForVariant(v, sX, sY);
    const varSeed = v.packing.seed ^ ((vi * 997 + lvl * 31337) | 0);
    const varRng = makeRng(varSeed);
    const regions = generatePackingLayout(cols, rows, {
        ...v.packing,
        seed: varSeed,
    });
    const filled = selectFilledRegions(regions, v, varRng);
    const out: number[] = [];

    if (lvl === 0) {
        if (layout.kind === "sin" || layout.kind === "z2half") {
            const kind: ConformalMapKind =
                layout.kind === "sin" ? "sin" : "z2half";
            const sm = layout.m;
            for (const region of regions) {
                if (!filled.has(region.id)) continue;
                for (const cell of region.cells) {
                    const corners = conformalQuadCornersVariantSpace(
                        kind,
                        cell.col,
                        cell.row,
                        sm,
                    );
                    out.push(aabbAreaFromPoints(corners, M));
                }
            }
            return out;
        }
        for (const region of regions) {
            if (!filled.has(region.id)) continue;
            for (const _cell of region.cells) {
                out.push(aabbAreaFromRect(cw, ch, M));
            }
        }
        return out;
    }

    const subGroup = levelGroups[lvl - 1];
    for (const region of regions) {
        if (!filled.has(region.id)) continue;
        if (!subGroup?.variants.length) continue;

        const subVi = Math.floor(globalRng() * subGroup.variants.length);
        const subV = subGroup.variants[subVi]!;
        const subSpX = noSpacing ? 0 : subV.spacingX;
        const subSpY = noSpacing ? 0 : subV.spacingY;
        const subW = gridDim(subV.columns, subV.cellWidth, subSpX);
        const subH = gridDim(subV.rows, subV.cellHeight, subSpY);

        const bbox = regionBBox(region, cw, ch, sX, sY, cols, rows, layout);
        const scaleX = subW > 0 ? bbox.width / subW : 1;
        const scaleY = subH > 0 ? bbox.height / subH : 1;
        let sx: number;
        let sy: number;
        if (!keepAspectRatio) {
            sx = scaleX;
            sy = scaleY;
        } else {
            const sc = containAspectFit
                ? Math.min(scaleX, scaleY)
                : Math.max(scaleX, scaleY);
            sx = sy = sc;
        }

        const doMirror =
            mirrorChildrenEnabled &&
            globalRng() * 100 < mirrorChildrenPercent;
        const doRotate =
            rotateChildrenEnabled &&
            globalRng() * 100 < rotateChildrenPercent;
        const rotateDeg: 0 | 90 | 180 | 270 = doRotate
            ? ([90, 180, 270][Math.floor(globalRng() * 3)] as 90 | 180 | 270)
            : 0;

        const layoutRotDeg = polarSpiralEmbedRotation
            ? layoutEmbeddingRotationDeg(
                  layout,
                  region,
                  cols,
                  rows,
                  cw,
                  ch,
                  sX,
                  sY,
              )
            : 0;
        const R = matRotate2dDeg(layoutRotDeg);
        const L = composeChildEmbeddingLinear(sx, sy, rotateDeg, doMirror);
        const M2 = matMul(M, matMul(R, L));
        out.push(
            ...collectRenderedRectAreas(globalRng, config, lvl - 1, subVi, M2),
        );
    }
    return out;
}

function emitGradientNestedSvg(
    globalRng: () => number,
    config: NestedGridConfig,
    lvl: number,
    vi: number,
    M: Mat2,
    areas: number[],
    areaIdx: { i: number },
    minA: number,
    maxA: number,
): string {
    const {
        levelGroups,
        noSpacing,
        keepAspectRatio,
        containAspectFit,
        mirrorChildrenEnabled,
        mirrorChildrenPercent,
        rotateChildrenEnabled,
        rotateChildrenPercent,
        polarSpiralEmbedRotation,
    } = config;
    const group = levelGroups[lvl];
    const v = group?.variants[vi];
    if (!v) return "";

    const {
        cellWidth: cw,
        cellHeight: ch,
        spacingX: rawSX,
        spacingY: rawSY,
        columns: cols,
        rows,
    } = v;
    const sX = noSpacing ? 0 : rawSX;
    const sY = noSpacing ? 0 : rawSY;
    const layout = variantLayoutExtraForVariant(v, sX, sY);
    const varSeed = v.packing.seed ^ ((vi * 997 + lvl * 31337) | 0);
    const varRng = makeRng(varSeed);
    const regions = generatePackingLayout(cols, rows, {
        ...v.packing,
        seed: varSeed,
    });
    const filled = selectFilledRegions(regions, v, varRng);

    if (lvl === 0) {
        const span = maxA - minA;
        const parts: string[] = [];
        if (layout.kind === "sin" || layout.kind === "z2half") {
            const kind: ConformalMapKind =
                layout.kind === "sin" ? "sin" : "z2half";
            const sm = layout.m;
            for (const region of regions) {
                if (!filled.has(region.id)) continue;
                for (const cell of region.cells) {
                    const ai = areas[areaIdx.i]!;
                    areaIdx.i += 1;
                    const t =
                        span <= 0 ? 0.5 : (ai - minA) / span;
                    const rectFill = sampleGradientStops(
                        config.leafGradientStops,
                        t,
                    );
                    const corners = conformalQuadCornersVariantSpace(
                        kind,
                        cell.col,
                        cell.row,
                        sm,
                    );
                    parts.push(
                        leafSinCellSvg(corners, rectFill, config.leafCellShape),
                    );
                }
            }
            return parts.join("");
        }
        for (const region of regions) {
            if (!filled.has(region.id)) continue;
            for (const cell of region.cells) {
                const ai = areas[areaIdx.i]!;
                areaIdx.i += 1;
                const t =
                    span <= 0 ? 0.5 : (ai - minA) / span;
                const rectFill = sampleGradientStops(
                    config.leafGradientStops,
                    t,
                );
                const rp = rotatedLeafPlacement(
                    layout,
                    cell.col,
                    cell.row,
                    cols,
                    rows,
                    cw,
                    ch,
                    sX,
                    sY,
                );
                if (rp) {
                    parts.push(
                        leafCellSvgPlaced(
                            cw,
                            ch,
                            rectFill,
                            config.leafCellShape,
                            {
                                kind: "rotated",
                                cx: rp.cx,
                                cy: rp.cy,
                                rotDeg: rp.rotDeg,
                            },
                        ),
                    );
                } else {
                    const { x, y } = cellPx(
                        cell.col,
                        cell.row,
                        cw,
                        ch,
                        sX,
                        sY,
                    );
                    parts.push(
                        leafCellSvgPlaced(
                            cw,
                            ch,
                            rectFill,
                            config.leafCellShape,
                            {
                                kind: "rect",
                                x,
                                y,
                            },
                        ),
                    );
                }
            }
        }
        return parts.join("");
    }

    const subGroup = levelGroups[lvl - 1];
    const pieces: string[] = [];

    for (const region of regions) {
        if (!filled.has(region.id)) continue;
        if (!subGroup?.variants.length) continue;

        const subVi = Math.floor(globalRng() * subGroup.variants.length);
        const subV = subGroup.variants[subVi]!;
        const subSpX = noSpacing ? 0 : subV.spacingX;
        const subSpY = noSpacing ? 0 : subV.spacingY;
        const subW = gridDim(subV.columns, subV.cellWidth, subSpX);
        const subH = gridDim(subV.rows, subV.cellHeight, subSpY);

        const bbox = regionBBox(region, cw, ch, sX, sY, cols, rows, layout);
        const scaleX = subW > 0 ? bbox.width / subW : 1;
        const scaleY = subH > 0 ? bbox.height / subH : 1;
        let sx: number;
        let sy: number;
        if (!keepAspectRatio) {
            sx = scaleX;
            sy = scaleY;
        } else {
            const sc = containAspectFit
                ? Math.min(scaleX, scaleY)
                : Math.max(scaleX, scaleY);
            sx = sy = sc;
        }

        const layoutRotDeg = polarSpiralEmbedRotation
            ? layoutEmbeddingRotationDeg(
                  layout,
                  region,
                  cols,
                  rows,
                  cw,
                  ch,
                  sX,
                  sY,
              )
            : 0;
        const innerTransform = (() => {
            let base: string;
            if (!keepAspectRatio) {
                base = `translate(${fmt(bbox.x)},${fmt(
                    bbox.y,
                )}) scale(${fmt(scaleX)},${fmt(scaleY)})`;
            } else {
                const scale = containAspectFit
                    ? Math.min(scaleX, scaleY)
                    : Math.max(scaleX, scaleY);
                const scaledW = subW * scale;
                const scaledH = subH * scale;
                const offsetX = bbox.x + (bbox.width - scaledW) / 2;
                const offsetY = bbox.y + (bbox.height - scaledH) / 2;
                base = `translate(${fmt(offsetX)},${fmt(
                    offsetY,
                )}) scale(${fmt(scale)})`;
            }
            if (!polarSpiralEmbedRotation) return base;
            const { cx: embCx, cy: embCy } = embeddingPlacementCenter(
                bbox,
                keepAspectRatio,
                containAspectFit,
                subW,
                subH,
                scaleX,
                scaleY,
            );
            return wrapEmbeddingTransform(embCx, embCy, layoutRotDeg, base);
        })();

        const doMirror =
            mirrorChildrenEnabled &&
            globalRng() * 100 < mirrorChildrenPercent;
        const doRotate =
            rotateChildrenEnabled &&
            globalRng() * 100 < rotateChildrenPercent;
        const rotateDeg: 0 | 90 | 180 | 270 = doRotate
            ? ([90, 180, 270][Math.floor(globalRng() * 3)] as 90 | 180 | 270)
            : 0;

        const R = matRotate2dDeg(layoutRotDeg);
        const L = composeChildEmbeddingLinear(sx, sy, rotateDeg, doMirror);
        const M2 = matMul(M, matMul(R, L));

        const inner = emitGradientNestedSvg(
            globalRng,
            config,
            lvl - 1,
            subVi,
            M2,
            areas,
            areaIdx,
            minA,
            maxA,
        );
        const decorated = wrapChildGridDecorations(
            subW,
            subH,
            inner,
            rotateDeg,
            doMirror,
        );
        pieces.push(
            `<g transform="${innerTransform}">${decorated}</g>`,
        );
    }
    return pieces.join("");
}

function buildNestedGridSvgGradientByRenderedRect(
    config: NestedGridConfig,
): string {
    const { nestingLevels, levelGroups, seed, noSpacing } = config;
    const rootLvl = nestingLevels - 1;
    const rootVariant = levelGroups[rootLvl]?.variants[0];
    if (!rootVariant) {
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"></svg>`;
    }

    const rootSpX = noSpacing ? 0 : rootVariant.spacingX;
    const rootSpY = noSpacing ? 0 : rootVariant.spacingY;
    const rootSize = variantLayoutSize(rootVariant, rootSpX, rootSpY);
    const rootW = rootSize.w;
    const rootH = rootSize.h;

    const rng1 = makeRng(seed);
    fastForwardGlobalRngBeforeRootVariant0(rng1, config);
    const areas = collectRenderedRectAreas(
        rng1,
        config,
        rootLvl,
        0,
        matIdentity(),
    );
    const minA = areas.length ? Math.min(...areas) : 0;
    const maxA = areas.length ? Math.max(...areas) : 0;

    const rng2 = makeRng(seed);
    fastForwardGlobalRngBeforeRootVariant0(rng2, config);
    const areaIdx = { i: 0 };
    const rootContent = emitGradientNestedSvg(
        rng2,
        config,
        rootLvl,
        0,
        matIdentity(),
        areas,
        areaIdx,
        minA,
        maxA,
    );

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fmt(rootW)} ${fmt(rootH)}">\n  ${rootContent}\n</svg>`;
}

// ─── Region probability & density selection ───────────────────────────────────

function regionProbability(
    region: PackRegion,
    variant: VariantConfig,
    rng: () => number,
): number {
    const {
        columns: cols,
        rows,
        fillPattern,
        startPosition,
        endPosition,
        noise,
        dithering,
    } = variant;
    const centCol = (region.minCol + region.maxCol) / 2;
    const centRow = (region.minRow + region.maxRow) / 2;

    let prob = 1;
    if (fillPattern === "linear-gradient") {
        prob = calculateLinearGradientProbability(
            centCol,
            centRow,
            startPosition,
            endPosition,
            cols,
            rows,
        );
    } else if (fillPattern === "radial-gradient") {
        prob = calculateRadialGradientProbability(
            centCol,
            centRow,
            startPosition,
            cols,
            rows,
        );
    } else if (fillPattern === "reverse-radial-gradient") {
        prob = calculateReverseRadialGradientProbability(
            centCol,
            centRow,
            startPosition,
            cols,
            rows,
        );
    } else if (fillPattern === "perlin-noise") {
        prob = calculatePerlinNoiseProbability(centCol, centRow, cols, rows);
    }

    if (noise > 0) {
        prob = Math.max(0, Math.min(1, prob + (noise / 100) * (rng() * 2 - 1)));
    }
    prob = applyDitheringToProbability(
        prob,
        dithering,
        Math.round(centCol),
        Math.round(centRow),
    );
    return prob;
}

function selectFilledRegions(
    regions: PackRegion[],
    variant: VariantConfig,
    rng: () => number,
): Set<string> {
    const totalCells = Math.max(1, variant.columns) * Math.max(1, variant.rows);
    const target = Math.round((variant.density / 100) * totalCells);

    const scored = regions
        .map((r) => ({
            id: r.id,
            cellCount: r.cells.length,
            prob: regionProbability(r, variant, rng),
        }))
        .sort((a, b) => b.prob - a.prob);

    const filled = new Set<string>();
    let count = 0;
    for (const r of scored) {
        if (count >= target) break;
        filled.add(r.id);
        count += r.cellCount;
    }
    return filled;
}

/** Wrap content (in 0…subW × 0…subH): rotate around center, then horizontal mirror around center. */
function wrapChildGridDecorations(
    subW: number,
    subH: number,
    subContent: string,
    rotateDeg: 0 | 90 | 180 | 270,
    doMirror: boolean,
): string {
    let inner = subContent;
    if (rotateDeg !== 0) {
        const cx = subW / 2;
        const cy = subH / 2;
        inner = `<g transform="translate(${fmt(cx)},${fmt(
            cy,
        )}) rotate(${rotateDeg}) translate(${fmt(-cx)},${fmt(-cy)})">${inner}</g>`;
    }
    if (doMirror) {
        const cx = subW / 2;
        const cy = subH / 2;
        inner = `<g transform="translate(${fmt(cx)},${fmt(
            cy,
        )}) scale(-1,1) translate(${fmt(-cx)},${fmt(-cy)})">${inner}</g>`;
    }
    return inner;
}

// ─── SVG builder ─────────────────────────────────────────────────────────────

export function buildNestedGridSvg(config: NestedGridConfig): string {
    const {
        nestingLevels,
        levelGroups,
        leafColorMode,
        leafFill,
        seed,
        keepAspectRatio,
        containAspectFit,
        mirrorChildrenEnabled,
        mirrorChildrenPercent,
        rotateChildrenEnabled,
        rotateChildrenPercent,
        noSpacing,
        polarSpiralEmbedRotation,
    } = config;

    if (!levelGroups.length || !levelGroups[0]?.variants.length) {
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"></svg>`;
    }

    if (leafColorMode === "gradient-by-size") {
        return buildNestedGridSvgGradientByRenderedRect(config);
    }

    const globalRng = makeRng(seed);

    // variantContent[lvl][vi] = pre-built SVG content string (in variant coords)
    const variantContent: string[][] = [];

    for (let lvl = 0; lvl < nestingLevels; lvl++) {
        const group = levelGroups[lvl];
        if (!group?.variants.length) {
            variantContent.push([]);
            continue;
        }
        const lvlContent: string[] = [];

        for (let vi = 0; vi < group.variants.length; vi++) {
            const v = group.variants[vi];
            const {
                cellWidth: cw,
                cellHeight: ch,
                spacingX: rawSX,
                spacingY: rawSY,
                columns: cols,
                rows,
            } = v;
            const sX = noSpacing ? 0 : rawSX;
            const sY = noSpacing ? 0 : rawSY;
            const layout = variantLayoutExtraForVariant(v, sX, sY);

            const varSeed = v.packing.seed ^ ((vi * 997 + lvl * 31337) | 0);
            const varRng = makeRng(varSeed);
            const regions = generatePackingLayout(cols, rows, {
                ...v.packing,
                seed: varSeed,
            });
            const filled = selectFilledRegions(regions, v, varRng);

            const parts: string[] = [];

            for (const region of regions) {
                if (!filled.has(region.id)) continue;

                if (lvl === 0) {
                    const rectFill = leafFill;
                    if (layout.kind === "sin" || layout.kind === "z2half") {
                        const kind: ConformalMapKind =
                            layout.kind === "sin" ? "sin" : "z2half";
                        const sm = layout.m;
                        for (const cell of region.cells) {
                            const corners = conformalQuadCornersVariantSpace(
                                kind,
                                cell.col,
                                cell.row,
                                sm,
                            );
                            parts.push(
                                leafSinCellSvg(
                                    corners,
                                    rectFill,
                                    config.leafCellShape,
                                ),
                            );
                        }
                    } else {
                        for (const cell of region.cells) {
                            const rp = rotatedLeafPlacement(
                                layout,
                                cell.col,
                                cell.row,
                                cols,
                                rows,
                                cw,
                                ch,
                                sX,
                                sY,
                            );
                            if (rp) {
                                parts.push(
                                    leafCellSvgPlaced(
                                        cw,
                                        ch,
                                        rectFill,
                                        config.leafCellShape,
                                        {
                                            kind: "rotated",
                                            cx: rp.cx,
                                            cy: rp.cy,
                                            rotDeg: rp.rotDeg,
                                        },
                                    ),
                                );
                            } else {
                                const { x, y } = cellPx(
                                    cell.col,
                                    cell.row,
                                    cw,
                                    ch,
                                    sX,
                                    sY,
                                );
                                parts.push(
                                    leafCellSvgPlaced(
                                        cw,
                                        ch,
                                        rectFill,
                                        config.leafCellShape,
                                        {
                                            kind: "rect",
                                            x,
                                            y,
                                        },
                                    ),
                                );
                            }
                        }
                    }
                } else {
                    // Non-leaf: embed sub-grid content scaled to this region's bbox
                    const subGroup = levelGroups[lvl - 1];
                    if (!subGroup?.variants.length) continue;

                    const subVi = Math.floor(
                        globalRng() * subGroup.variants.length,
                    );
                    const subContent = variantContent[lvl - 1]?.[subVi] ?? "";
                    const subV = subGroup.variants[subVi];
                    const subSpX = noSpacing ? 0 : subV.spacingX;
                    const subSpY = noSpacing ? 0 : subV.spacingY;
                    const subW = gridDim(subV.columns, subV.cellWidth, subSpX);
                    const subH = gridDim(subV.rows, subV.cellHeight, subSpY);

                    const bbox = regionBBox(
                        region,
                        cw,
                        ch,
                        sX,
                        sY,
                        cols,
                        rows,
                        layout,
                    );
                    const scaleX = subW > 0 ? bbox.width / subW : 1;
                    const scaleY = subH > 0 ? bbox.height / subH : 1;

                    const layoutRotDeg = polarSpiralEmbedRotation
                        ? layoutEmbeddingRotationDeg(
                              layout,
                              region,
                              cols,
                              rows,
                              cw,
                              ch,
                              sX,
                              sY,
                          )
                        : 0;
                    const innerTransform = (() => {
                        let base: string;
                        if (!keepAspectRatio) {
                            base = `translate(${fmt(bbox.x)},${fmt(
                                bbox.y,
                            )}) scale(${fmt(scaleX)},${fmt(scaleY)})`;
                        } else {
                            const scale = containAspectFit
                                ? Math.min(scaleX, scaleY)
                                : Math.max(scaleX, scaleY);
                            const scaledW = subW * scale;
                            const scaledH = subH * scale;
                            const offsetX = bbox.x + (bbox.width - scaledW) / 2;
                            const offsetY = bbox.y + (bbox.height - scaledH) / 2;
                            base = `translate(${fmt(offsetX)},${fmt(
                                offsetY,
                            )}) scale(${fmt(scale)})`;
                        }
                        if (!polarSpiralEmbedRotation) return base;
                        const { cx: embCx, cy: embCy } = embeddingPlacementCenter(
                            bbox,
                            keepAspectRatio,
                            containAspectFit,
                            subW,
                            subH,
                            scaleX,
                            scaleY,
                        );
                        return wrapEmbeddingTransform(
                            embCx,
                            embCy,
                            layoutRotDeg,
                            base,
                        );
                    })();

                    const doMirror =
                        mirrorChildrenEnabled &&
                        globalRng() * 100 < mirrorChildrenPercent;
                    const doRotate =
                        rotateChildrenEnabled &&
                        globalRng() * 100 < rotateChildrenPercent;
                    const rotateDeg: 0 | 90 | 180 | 270 = doRotate
                        ? ([90, 180, 270][Math.floor(globalRng() * 3)] as
                              | 90
                              | 180
                              | 270)
                        : 0;

                    const decorated = wrapChildGridDecorations(
                        subW,
                        subH,
                        subContent,
                        rotateDeg,
                        doMirror,
                    );

                    parts.push(
                        `<g transform="${innerTransform}">${decorated}</g>`,
                    );
                }
            }

            lvlContent.push(parts.join(""));
        }

        variantContent.push(lvlContent);
    }

    const rootLvl = nestingLevels - 1;
    const rootVariant = levelGroups[rootLvl]?.variants[0];
    if (!rootVariant) {
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"></svg>`;
    }

    const rootSpX = noSpacing ? 0 : rootVariant.spacingX;
    const rootSpY = noSpacing ? 0 : rootVariant.spacingY;
    const rootWH = variantLayoutSize(rootVariant, rootSpX, rootSpY);
    const rootW = rootWH.w;
    const rootH = rootWH.h;
    const rootContent = variantContent[rootLvl]?.[0] ?? "";

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fmt(rootW)} ${fmt(rootH)}">\n  ${rootContent}\n</svg>`;
}

// ─── Default factory helpers ──────────────────────────────────────────────────

let _variantCounter = 0;

export function createDefaultVariant(lvlHint = 0): VariantConfig {
    const id = `v${_variantCounter++}`;
    return {
        id,
        gridShape: "rectangular",
        spiralTurns: 3,
        columns: lvlHint === 0 ? 4 : 6,
        rows: lvlHint === 0 ? 4 : 6,
        cellWidth: lvlHint === 0 ? 25 : 80,
        cellHeight: lvlHint === 0 ? 25 : 80,
        spacingX: 3,
        spacingY: 3,
        density: 75,
        fillPattern: "even",
        startPosition: "top-left",
        endPosition: "bottom-right",
        noise: 0,
        dithering: 0,
        packing: {
            maxPackSize: 4,
            probability: 25,
            seed: 42,
            irregularShapes: false,
        },
    };
}

export function createDefaultLevelGroup(lvl: number): LevelGroup {
    return { variants: [createDefaultVariant(lvl)] };
}

// ─── Randomize (existing variants only; ids preserved) ───────────────────────

const FILL_PATTERNS: FillPattern[] = [
    "even",
    "linear-gradient",
    "radial-gradient",
    "reverse-radial-gradient",
    "perlin-noise",
];

const NESTED_POSITIONS = [
    "top-left",
    "top",
    "top-right",
    "left",
    "center",
    "right",
    "bottom-left",
    "bottom",
    "bottom-right",
] as const;

function rndInt(min: number, max: number): number {
    const lo = Math.ceil(min);
    const hi = Math.floor(max);
    return Math.floor(Math.random() * (hi - lo + 1)) + lo;
}

function pick<const T extends readonly string[]>(arr: T): T[number] {
    return arr[Math.floor(Math.random() * arr.length)]!;
}

export type RandomizeGridCapMode =
    | "none"
    | "global"
    | "by-level"
    | "exponential";

export interface RandomizeVariantOptions {
    /** When true, rows = columns and cellHeight = cellWidth. */
    squareGrid?: boolean;
    /** Nesting level index (0 = leaf) — used for by-level and exponential caps. */
    levelIndex?: number;
    /** How random column/row upper bounds are chosen per level. */
    randomizeGridCapMode?: RandomizeGridCapMode;
    /**
     * Base N: global = max dim; by-level = N × (level + 1); exponential = N^(level+1).
     */
    randomizeGridCapBase?: number;
    /** When true, random columns/rows use at least randomizeGridMinValue (clamped to cap). */
    randomizeGridMinEnabled?: boolean;
    randomizeGridMinValue?: number;
    /** Variant `id`s to leave unchanged when randomizing. */
    lockedVariantIds?: ReadonlySet<string>;
}

const RANDOM_GRID_DEFAULT_HI = 24;
const RANDOM_GRID_EXPONENTIAL_CAP = 500;

function resolveRandomGridHi(opts?: RandomizeVariantOptions): number {
    const mode = opts?.randomizeGridCapMode ?? "none";
    const baseRaw = opts?.randomizeGridCapBase ?? 4;
    const base = Math.max(2, Math.floor(baseRaw));
    const level = Math.max(0, opts?.levelIndex ?? 0);

    switch (mode) {
        case "none":
            return RANDOM_GRID_DEFAULT_HI;
        case "global":
            return Math.max(2, Math.floor(baseRaw));
        case "by-level":
            return Math.max(2, base * (level + 1));
        case "exponential": {
            const hi = Math.floor(Math.pow(base, level + 1));
            return Math.max(2, Math.min(RANDOM_GRID_EXPONENTIAL_CAP, hi));
        }
        default:
            return RANDOM_GRID_DEFAULT_HI;
    }
}

/** Randomizes grid, distribution, and packing for one variant; keeps `id`. */
export function randomizeVariantParams(
    v: VariantConfig,
    options?: RandomizeVariantOptions,
): VariantConfig {
    const square = options?.squareGrid ?? false;
    const gridHi = resolveRandomGridHi(options);
    const minRequested = options?.randomizeGridMinEnabled
        ? Math.max(2, Math.floor(options.randomizeGridMinValue ?? 2))
        : 2;
    const gridLo = Math.min(minRequested, gridHi);
    const columns = rndInt(gridLo, gridHi);
    const rows = square ? columns : rndInt(gridLo, gridHi);
    const cellWidth = rndInt(10, 140);
    const cellHeight = square ? cellWidth : rndInt(10, 140);
    const cellMax = Math.min(12, columns * rows);
    return {
        ...v,
        columns,
        rows,
        cellWidth,
        cellHeight,
        spacingX: rndInt(0, 24),
        spacingY: rndInt(0, 24),
        density: rndInt(5, 100),
        fillPattern: pick(FILL_PATTERNS),
        startPosition: pick(NESTED_POSITIONS),
        endPosition: pick(NESTED_POSITIONS),
        noise: rndInt(0, 100),
        dithering: rndInt(0, 100),
        packing: {
            maxPackSize: rndInt(1, Math.max(1, cellMax)),
            probability: rndInt(0, 100),
            seed: rndInt(0, 2_147_483_647),
            irregularShapes: Math.random() < 0.45,
        },
    };
}

/** Deep-randomizes every variant in every level group; structure/count unchanged. */
export function randomizeAllVariantParams(
    groups: LevelGroup[],
    options?: RandomizeVariantOptions,
): LevelGroup[] {
    const locked = options?.lockedVariantIds;
    return groups.map((g, levelIdx) => ({
        variants: g.variants.map((v) => {
            if (locked?.has(v.id)) {
                return v;
            }
            return randomizeVariantParams(v, {
                ...options,
                levelIndex: levelIdx,
            });
        }),
    }));
}
